/* eslint-disable jsx-a11y/alt-text */
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import {
  ChatContainer,
  Message,
  MessageInput,
  MessageList,
  TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import { Modal, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { apiClient } from '../../utilities/apiClient';
import { getCurrentUserId } from '../../utilities/auth';
import { getSocket } from '../../utilities/socket';
import {
  confirmConciergeDraft,
  createMessageInChat,
  declineConciergeDraft,
  fetchChatMessages,
  fetchRealTimeChatMessages,
  fetchSingleConversation,
  uploadAssetForChat,
} from '../../containers/ChatPage/sagas/saga';
import { PostDraftCard } from '../PostDraftCard/PostDraftCard';

// Pinned at build time from `REACT_APP_CONCIERGE_BOT_USER_ID`. If missing,
// the overlay never opens (the parent renders nothing). Same UUID lives in
// `.env.{dev,prod}` as `CONCIERGE_BOT_USER_ID` so the backend listener can
// match.
const BOT_ID = process.env.REACT_APP_CONCIERGE_BOT_USER_ID;

/**
 * The Rovo-style right-edge slide-in for the dresez concierge bot. Same
 * underlying conversation as the row that auto-appears in `/chats` (the
 * bot is a regular user, the conversation is a regular conversation), so
 * both surfaces stay in sync over the same Socket.IO wire — this component
 * just calls the existing ChatPage sagas to fetch + subscribe.
 *
 * Stays mounted across overlay open/close so chat history isn't refetched
 * each time the user pops it open. The MUI Modal handles its own escape/
 * backdrop dismissal.
 */
export const ConciergeOverlay = ({ open, onClose }) => {
  const intl = useIntl();
  const currentUser = useSelector((state) => state.user);
  const me = currentUser?.user?.id || getCurrentUserId();
  const [chatId, setChatId] = useState(null);
  const [messageList, setMessageList] = useState([]);
  const [draft, setDraft] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [bootError, setBootError] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  const msgListRef = useRef();
  const fileInputRef = useRef();

  const placeholder = intl.formatMessage({
    id: 'concierge.placeholder',
    defaultMessage: 'Ask me anything…',
  });
  const headerTitle = intl.formatMessage({
    id: 'concierge.header.title',
    defaultMessage: 'dresez concierge',
  });
  const closeLabel = intl.formatMessage({
    id: 'concierge.aria.close',
    defaultMessage: 'Close concierge',
  });
  const welcome = intl.formatMessage({
    id: 'concierge.welcome',
    defaultMessage:
      "Hi! I'm the dresez concierge. Ask me to find items, or anything else about dresez.",
  });
  const typingLabel = intl.formatMessage({
    id: 'concierge.typing',
    defaultMessage: 'dresez concierge is typing',
  });

  // Resolve the bot conversation id every time the overlay opens. The
  // POST /conversations endpoint is idempotent (getOrCreate) — it returns
  // the existing conversation for the (me, bot) pair or creates one if
  // missing — so always asking the server is safe AND avoids a stale
  // localStorage cache from a previous browser session (different uid
  // cookie) pointing to a conversation the current user can't read. The
  // cached-chatId approach we used before silently 403'd in that case.
  //
  // Also primes getSocket() before chatId resolves so the WebSocket
  // handshake races with /conversations rather than after it. The first
  // home-screen FAB tap of a session used to land before the socket ever
  // connected (the socket only initialised when fetchRealTimeChatMessages
  // ran AFTER chatId was set), which meant the user's own messages
  // wouldn't echo back over the socket and the bot's reply was invisible
  // until they navigated to /chats (which independently initialised the
  // socket through useFetchLatestMessage). Calling getSocket() here lets
  // the handshake start the moment the FAB is tapped.
  useEffect(() => {
    if (!open || !me || !BOT_ID) return;
    if (chatId) return;
    let cancelled = false;
    setIsResolving(true);
    setBootError(null);
    // Fire-and-forget — getSocket() returns a singleton, so this just
    // ensures the connection is being established. Subsequent callers
    // (fetchRealTimeChatMessages below) get the same socket back.
    try { getSocket(); } catch { /* socket is best-effort */ }
    apiClient
      .post('/conversations', { receiverId: BOT_ID })
      .then((res) => {
        if (cancelled) return;
        const id = res?.data?.conversation?.id;
        if (id) setChatId(id);
        else setBootError('unable to start');
      })
      .catch((err) => {
        if (cancelled) return;
        setBootError(err?.response?.data?.message || err?.message || 'unable to start');
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, chatId, me]);

  // Refetch the conversation and trust the server's chronological view.
  // Drops any optimistic _local rows still in state — they're either
  // already represented (in which case keeping them duplicates) or POST
  // is still mid-flight (in which case the swap path below handles it
  // when POST resolves: tempId no longer present → append the real row).
  //
  // The earlier "preserve _local" variant of this helper appended any
  // lingering _local rows to the END of chronological, which on slow-POST
  // sessions placed the stale optimistic AFTER the bot's reply — the
  // isTyping useEffect then read the optimistic as `last`, kept the
  // typing indicator on, and stuck users at "concierge is typing" even
  // though the bot's reply was clearly visible above. (Reported 2026-06-02.)
  const refetchHistory = useCallback(
    (cid) => {
      if (!cid) return Promise.resolve();
      return fetchChatMessages({ page: 0, docLimit: 50, chatId: cid })
        .then((msgs) => setMessageList([...msgs].reverse()))
        .catch(() => null);
    },
    [],
  );

  // Initial history fetch + Socket.IO subscription. Runs once per chatId
  // change — the same wire ChatPage uses, so the overlay and `/chats`
  // mirror each other automatically.
  useEffect(() => {
    if (!chatId || !me) return;
    let cancelled = false;
    fetchSingleConversation({ chatId, userId: me }).catch(() => null);
    if (!cancelled) refetchHistory(chatId);
    const unsub = fetchRealTimeChatMessages({
      chatId,
      setMessage: (m) => {
        setMessageList((prev) => {
          // Existing id-based dedupe — handles socket re-emit and the
          // case where POST resolved first and the swapped row is already
          // present.
          const idx = prev.findIndex((x) => x.id === m.id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = m;
            return next;
          }
          // If this socket event is the echo of MY own just-sent message,
          // it carries a real UUID while our optimistic _local row carries
          // a temp 'local-…' id, so id-based dedupe missed. Match by
          // sender + value and swap the optimistic in-place. Without this
          // the user sees their message twice (once as the optimistic,
          // once as the socket echo) when the WebSocket is faster than
          // the POST round-trip — happened consistently after the
          // 2026-06-02 nginx /socket.io fix because the real-time path
          // now wins the race more often than it used to.
          if (m.senderId === me) {
            const optIdx = prev.findIndex(
              (x) => x._local && x.senderId === m.senderId && x.value === m.value,
            );
            if (optIdx >= 0) {
              const next = [...prev];
              next[optIdx] = m;
              return next;
            }
          }
          return [...prev, m];
        });
      },
    });
    return () => {
      cancelled = true;
      if (typeof unsub === 'function') unsub();
    };
  }, [chatId, me, refetchHistory]);

  // Catch up on missed events whenever the overlay reopens. Socket.IO does
  // not queue events for disconnected rooms, so any messages the bot
  // emitted while the user's tab was backgrounded / phone screen off /
  // socket dropped never reach the live stream. Refetching on reopen
  // brings the overlay back in sync with the DB.
  useEffect(() => {
    if (!open || !chatId) return;
    refetchHistory(chatId);
  }, [open, chatId, refetchHistory]);

  // Stuck-typing fallback. If the indicator stays on for longer than a
  // generous reply window (bot p99 with retry is ~10s), the bot probably
  // replied while the socket was disconnected. Refetch to surface the
  // reply; the isTyping useEffect below will clear the indicator naturally
  // once the new last-message is from the bot. Bounded — single refetch
  // per stuck window, not a polling loop.
  useEffect(() => {
    if (!isTyping || !chatId) return;
    const handle = setTimeout(() => {
      refetchHistory(chatId);
    }, 15_000);
    return () => clearTimeout(handle);
  }, [isTyping, chatId, refetchHistory]);

  // Typing indicator: derived from message timestamps, NOT array position.
  //
  // Earlier this useEffect checked `last = messageList[len-1]` and set
  // isTyping based on whether that last array element was from the user.
  // That made the indicator fragile to any duplicate or stale row landing
  // at the end of the array (refetch races, optimistic-local lingering
  // past POST swap, etc.) — symptom reported 2026-06-02 was "typing
  // indicator stuck on even though the bot's reply is clearly visible
  // above it." The right semantics: typing is on iff the user has spoken
  // more recently than the bot has, period. Position in the array is an
  // implementation detail; presence of a recent bot message is what
  // matters to the user.
  //
  // CLOCK_SKEW_MS guards against the optimistic-local rows that carry a
  // client-side Date.now() createdAt, which can sit a few seconds ahead
  // of the server clock on phones that haven't synced NTP recently. 5s
  // tolerance comfortably covers typical drift without delaying the
  // indicator's clear when a real reply lands.
  useEffect(() => {
    if (messageList.length === 0) {
      setIsTyping(false);
      return;
    }
    const CLOCK_SKEW_MS = 5_000;
    let latestUserAt = -Infinity;
    let latestBotAt = -Infinity;
    for (const m of messageList) {
      const t = m.createdAt ? new Date(m.createdAt).getTime() : 0;
      if (m.senderId === me) {
        if (t > latestUserAt) latestUserAt = t;
      } else if (t > latestBotAt) {
        latestBotAt = t;
      }
    }
    setIsTyping(latestUserAt - CLOCK_SKEW_MS > latestBotAt);
  }, [messageList, me]);

  useEffect(() => {
    msgListRef.current?.scrollToBottom();
  }, [messageList, isTyping]);

  // Reset transient state when the user closes the overlay so the next
  // open starts clean (no leftover draft, no stuck "isResolving" if the
  // previous attempt errored). chatId stays in component state so a quick
  // close/re-open doesn't re-POST /conversations.
  useEffect(() => {
    if (!open) {
      setDraft('');
      setBootError(null);
    }
  }, [open]);

  const onSend = async () => {
    if (!chatId || !draft.trim() || !BOT_ID) return;
    const value = draft.trim();
    setDraft('');

    // Optimistic append. The realtime subscription will fire shortly
    // afterwards with the real row from the server; the dedupe-by-id
    // logic above replaces this local row in place, so no duplicate
    // bubble renders. Marked _local so we can rollback on POST failure
    // even after a subsequent state update; the real row from the
    // server overwrites the entire object so the flag goes away.
    const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      id: tempId,
      conversationId: chatId,
      senderId: me,
      receiverId: BOT_ID,
      type: 'text',
      value,
      status: 'delivered',
      createdAt: new Date().toISOString(),
      _local: true,
    };
    setMessageList((prev) => [...prev, optimistic]);

    try {
      const created = await createMessageInChat({
        chatId,
        type: 'text',
        value,
        senderId: me,
        recieverId: BOT_ID,
      });
      // Reconcile the optimistic row with the server's authoritative
      // version. Three cases:
      //   1. Optimistic still in state (normal): swap tempId → created.
      //   2. Optimistic was swept by a refetch (15s timeout fired while
      //      POST was in flight) AND the real row is now in state from
      //      the refetch or socket echo: no-op.
      //   3. Optimistic was swept AND the real row isn't in state yet
      //      (rare race): append it.
      // The socket-subscription dedupe-by-id handles any later echo of
      // the same id without duplicating.
      if (created?.id) {
        setMessageList((prev) => {
          const tempIdx = prev.findIndex((m) => m.id === tempId);
          if (tempIdx >= 0) {
            const next = [...prev];
            next[tempIdx] = { ...created };
            return next;
          }
          if (prev.some((m) => m.id === created.id)) return prev;
          return [...prev, created];
        });
      }
    } catch {
      // Roll back the optimistic row so it doesn't sit there pretending
      // to have been sent. createMessageInChat already surfaced the
      // error via the global notification.
      setMessageList((prev) => prev.filter((m) => m.id !== tempId));
    }
  };

  // Paperclip → file picker → upload + create chat message per pick.
  // Uploads run in parallel; createMessageInChat fires per file in pick
  // order so the user sees the bubbles roughly in the order they picked.
  // The listener filter on the backend skips agent-loop triggers for
  // non-text messages, so the bot doesn't reply per upload — the user
  // types "post it" once they're ready.
  const onAttachClick = () => fileInputRef.current?.click();
  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file later
    if (!chatId || files.length === 0) return;
    await Promise.all(
      files.map(async (file) => {
        try {
          const url = await uploadAssetForChat(file);
          if (!url) return;
          const type = file.type.startsWith('video/') ? 'video' : 'image';
          await createMessageInChat({
            chatId,
            type,
            value: url,
            senderId: me,
            recieverId: BOT_ID,
          });
        } catch {
          // Already surfaced via showNotification; skip this file.
        }
      }),
    );
  };

  // No bot configured — the FAB shouldn't have rendered either, but be
  // safe: render nothing if the env var didn't land in the build.
  if (!BOT_ID) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="flex items-stretch justify-end"
      aria-labelledby="concierge-overlay-title"
    >
      <div
        className="bg-white dark:bg-[#1c1d22] dark:text-gray-100 w-full sm:w-[420px] h-full flex flex-col outline-none shadow-xl"
        // The right-edge slide makes the panel feel docked rather than floating.
        // Drop-shadow gives it a faint separator on dark mode where there's
        // no border colour change between panel and dimmed backdrop.
      >
        {/* Header */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200 dark:border-[#2a2c30]">
          <div className="w-8 h-8 rounded-full bg-[#3a6df0]/10 flex items-center justify-center mr-3">
            <SmartToyOutlinedIcon className="text-[#3a6df0]" fontSize="small" />
          </div>
          <div id="concierge-overlay-title" className="flex-1 font-semibold">
            {headerTitle}
          </div>
          <IconButton aria-label={closeLabel} onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        {/* Chat body */}
        <div className="flex-1 min-h-0 relative">
          {/* Real loading state while POST /conversations is in flight.
              Without this, the welcome bubble renders immediately but the
              input is sendDisabled (chatId still null) — looks like the
              overlay is "stuck" because nothing tells the user we're
              still wiring up. */}
          {isResolving && !chatId && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-[#1c1d22]/80">
              <CircularProgress size={28} />
            </div>
          )}
          <ChatContainer style={{ height: '100%' }}>
            <MessageList
              ref={msgListRef}
              autoScrollToBottom
              autoScrollToBottomOnMount
              className="chat-message-list"
              typingIndicator={
                isTyping ? <TypingIndicator content={typingLabel} /> : null
              }
            >
              {/* Synthetic welcome bubble — only when no real history.
                  Doesn't get persisted; the first real bot reply will
                  deliver a proper greeting inline. */}
              {messageList.length === 0 && (
                <Message
                  model={{
                    message: welcome,
                    direction: 'incoming',
                    position: 'single',
                    type: 'text',
                    sender: 'concierge',
                  }}
                />
              )}
              {messageList.map((item, index) => {
                const key = item.id || index;
                // Special-case the M4 message types BEFORE handing off to
                // chatscope's Message component. The plain text path falls
                // through to the existing chatscope render.
                if (item.type === 'post_draft') {
                  return (
                    <PostDraftCard
                      key={key}
                      item={item}
                      onConfirm={confirmConciergeDraft}
                      onDecline={declineConciergeDraft}
                    />
                  );
                }
                if (item.type === 'image' || item.type === 'video') {
                  const outgoing = item.senderId === me;
                  return (
                    <div
                      key={key}
                      className={`flex my-1 px-2 ${outgoing ? 'justify-end' : 'justify-start'}`}
                    >
                      {item.type === 'image' ? (
                        <img
                          src={item.value}
                          alt=""
                          className="max-w-[60%] max-h-[240px] rounded-lg object-cover bg-gray-100 dark:bg-[#2a2c30]"
                        />
                      ) : (
                        <video
                          src={item.value}
                          controls
                          className="max-w-[60%] max-h-[280px] rounded-lg bg-black"
                        />
                      )}
                    </div>
                  );
                }
                return (
                  <Message
                    key={key}
                    model={{
                      message: item.value,
                      direction: item.senderId === me ? 'outgoing' : 'incoming',
                      position: 'normal',
                      type: 'text',
                    }}
                  />
                );
              })}
              {bootError && (
                <Message
                  model={{
                    message: bootError,
                    direction: 'incoming',
                    position: 'single',
                    type: 'text',
                  }}
                />
              )}
            </MessageList>
            <MessageInput
              attachButton
              placeholder={placeholder}
              onSend={onSend}
              onChange={(_val, text) => setDraft(text)}
              value={draft}
              sendDisabled={!chatId || !draft.trim()}
              onAttachClick={onAttachClick}
            />
          </ChatContainer>
        </div>
        {/* Hidden file picker invoked by the chatscope paperclip. multiple
            so the user can attach a few photos in one go; accept covers
            image/* and video/* — the per-kind size caps live server-side
            (IMAGE_MAX_BYTES / VIDEO_MAX_BYTES) and surface via the
            existing snackbar if they fire. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={onFilesPicked}
        />
      </div>
    </Modal>
  );
};

// Defaulting to named export keeps it consistent with the surrounding
// components folder (most use named exports).
export default ConciergeOverlay;
